import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { BASE_URL } from "../../env";
import { useAdminAuth } from "./AdminAuthProvider";
import { adminLoginValidation } from "./Validations";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { FaUser, FaLock } from "react-icons/fa";
import gsap from "gsap";

export default function LoginForm() {
  const navigate = useNavigate();
  const { adminLogin } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);

  const formRef = useRef(null);
  const infoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline();

    // Smooth fade in of the entire page background canvas
    tl.fromTo(containerRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" }
    );

    // Liquid, premium slide-in from left-to-right of the login card (GPU-accelerated and clean-restored)
    tl.fromTo(formRef.current,
      { x: -120, opacity: 0, scale: 0.98 },
      { 
        x: 0, 
        opacity: 1, 
        scale: 1, 
        duration: 1.3, 
        ease: "power4.out",
        force3D: true, // Forces GPU hardware acceleration to prevent stuttering/getting stuck
        clearProps: "transform,scale" // Clears transform styles after completion so CSS hovers function cleanly
      },
      "-=0.3"
    );
  }, []);

  const togglePasswordVisibility = () =>
    setShowPassword((prev) => !prev);

  const [initialFormValues, setInitialFormValues] = useState({
    username: "",
    password: "",
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem("saved_login_credentials");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && (parsed.username || parsed.password)) {
          setInitialFormValues({
            username: parsed.username || "",
            password: parsed.password || "",
          });
        }
      }
    } catch (err) {
      console.log("Error loading saved credentials:", err);
    }
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: adminLoginValidation,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await axios.post(
          `${BASE_URL}/api/superadmin/login`,
          values
        );

        if (response.status === 200) {
          toast.success(response.data.message || "Login successful!");
          localStorage.setItem(
            "saved_login_credentials",
            JSON.stringify({
              username: values.username,
              password: values.password,
            })
          );
          adminLogin(response.data);
          resetForm();
          navigate("/dashboard");
        }
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const msg = error.response.data?.message;

          if (status === 401) {
            toast.error(msg || "Invalid username or password.");
          } else if (status === 404) {
            toast.error(msg || "User not found.");
          } else {
            toast.error(msg || "Login failed.");
          }
        } else if (error.request) {
          toast.error("Network error. Please check your connection.");
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      }
    },
  });

  return (
    <section className="login-page-container" ref={containerRef}>
      <ToastContainer position="top-center" />
      <div className="login-background">
        <img
          className="login-bg-img"
          src={require("../../assets/login-bg.png")}
          alt="Background"
        />
        <div className="bg-overlay"></div>
      </div>

      <div className="login-content-wrapper">
        <div className="login-glass-card" ref={formRef}>
          <form onSubmit={formik.handleSubmit}>
            {/* Header: Logo Only */}
            <div className="logo-section">
              <img src={require("../../assets/vv-logo.png")} className="login-logo" alt="VT Logo" />
            </div>

            {/* Welcome Message */}
            <div className="welcome-section">
              <h2 className="welcome-title">Welcome Back!</h2>
              <p className="welcome-subtitle">Sign in to continue to your account</p>
            </div>

            {/* Input fields with top labels and lock icon */}
            <div className="input-group">
              <div className="input-field-wrapper">
                <label className="input-field-label">User ID</label>
                <div className="input-box">
                  <FaUser className="input-icon-left" />
                  <input
                    type="text"
                    name="username"
                    autoCapitalize="words"
                    autoComplete="username"
                    placeholder="Enter your user ID"
                    required
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.username}
                  />
                </div>
                {formik.touched.username && formik.errors.username && (
                  <span className="error-text">{formik.errors.username}</span>
                )}
              </div>

              <div className="input-field-wrapper">
                <label className="input-field-label">Password</label>
                <div className="input-box">
                  <div className="password-toggle" onClick={togglePasswordVisibility}>
                    {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                  </div>
                  <FaLock className="input-icon-left" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    required
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                  />
                </div>
                {formik.touched.password && formik.errors.password && (
                  <span className="error-text">{formik.errors.password}</span>
                )}
              </div>
            </div>

            <button type="submit" className="login-button">
              Sign In
            </button>

            {/* Powered By Block inside the card */}
            <div className="card-powered-by">
              <div className="powered-divider">
                <span>POWERED BY</span>
              </div>
              <div className="powered-logo-container">
                <a
                  href="https://virtualtechnology.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={require("../../assets/logo.png")}
                    className="card-powered-logo"
                    alt="Virtual Technology Logo"
                  />
                </a>
              </div>
              <p className="tech-desc">Empowering Financial Freedom <span className="versionno">  Version 2.0.1 </span></p>
              <p className="tech-desc1">Copyright © {(new Date().getFullYear())} VirtualTechnology . All rights reserved.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}


{
  /* <form className="formForm " style={{display: 'none'}} onSubmit={formik.handleSubmit}>
        
<div className="formMainDiv">
  
  <div style={{padding:30}}>
    <h4 className="formH1">Hello Admin !</h4>
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid black",
        margin: 10,
      }}
    >
      <FaUser />
      <input
        className="formInput"
        type="text"
        placeholder="Username"
        required
        name="username"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.username}
      />
    </div>
    {formik.touched.username && formik.errors.username && (
      <span className="formError">{formik.errors.username}</span>
    )}

    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid black",
        margin: 10,
      }}
    >
      <RiLockPasswordFill />
      <input
        className="formInput"
        type={showPassword ? "text" : "password"}
        placeholder="Password"
        required
        name="password"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
      />
      <div
        onClick={togglePasswordVisibility}
        style={{ position: "absolute", right: 10, cursor: "pointer" }}
      >
        {showPassword ? (
          <AiFillEyeInvisible size={20} />
        ) : (
          <AiFillEye size={20} />
        )}
      </div>
    </div>

    {formik.touched.password && formik.errors.password && (
      <span className="formError">{formik.errors.password}</span>
    )}

    <button className="formButton btn btn-primary" type="submit">
      Login
    </button>
  </div>
</div>
</form> */
}